import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = "/api";

const A_COLORS = { A1: '#7B74E0', A2: '#4ADE80', A3: '#FBBF24', A4: '#F87171' };
const DIR_COLORS = { upgrade: '#4ADE80', downgrade: '#F87171', deferred: '#9CA3AF' };
const CONF_COLORS = { high: '#4ade80', medium: '#fbbf24', low: '#f87171' };

function ABadge({ cls }) {
  return (
    <span style={{ background: (A_COLORS[cls] || '#6b7280') + '22', color: A_COLORS[cls] || '#6b7280',
      padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{cls}</span>
  );
}

function TargetBadges({ targets }) {
  if (!targets) return null;
  return targets.split(',').map(t => (
    <span key={t} style={{ background: 'rgba(175,169,236,0.12)', color: 'var(--accent)',
      padding: '1px 6px', borderRadius: 8, fontSize: 10, marginRight: 3 }}>{t.trim()}</span>
  ));
}

export default function SourceCalibration() {
  const { authFetch, user, logout } = useAuth();
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [logs, setLogs] = useState([]);
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState({});
  const [showLegend, setShowLegend] = useState(true);

  const load = useCallback(() => {
    authFetch(`${API}/admin/calibration/sources`).then(r => r.json()).then(setSources).catch(() => {});
    authFetch(`${API}/admin/calibration/proposals?status=pending_approval`).then(r => r.json()).then(setProposals).catch(() => {});
    authFetch(`${API}/admin/calibration/log`).then(r => r.json()).then(setLogs).catch(() => {});
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const handleDecide = async (proposalId, decision) => {
    const note = notes[proposalId] || '';
    if (note.trim().length < 10) return;
    setSaving(s => ({ ...s, [proposalId]: true }));
    await authFetch(`${API}/admin/calibration/proposals/${proposalId}/decide`, {
      method: 'PUT', body: JSON.stringify({ decision, note }),
    });
    setSaving(s => ({ ...s, [proposalId]: false }));
    setNotes(n => { const copy = { ...n }; delete copy[proposalId]; return copy; });
    load();
  };

  return (
    <div className="admin-page">
      <header className="header">
        <div className="header-left">
          <span className="logo-text" style={{ cursor: 'pointer' }} onClick={() => navigate('/chat')}>ERMENE</span>
          <span className="logo-subtitle">Source Calibration</span>
        </div>
        <div className="header-right">
          <span className="header-subtitle">{user?.username}</span>
          <button className="theme-toggle" onClick={logout}>Esci</button>
        </div>
      </header>

      <div className="admin-content" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* SECTION 1 — Registry */}
        <div>
          <h3 style={{ margin: '0 0 12px 0', color: 'var(--text-primary)', fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Sorgenti Registrate ({sources.length})
          </h3>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Source</th><th>V</th><th>N</th><th>A</th><th>Weight</th><th>Targets</th><th>Role</th><th>Decay</th>
                </tr>
              </thead>
              <tbody>
                {sources.map(s => (
                  <tr key={s.source_id}>
                    <td style={{ fontWeight: 500, fontSize: 12 }}>{s.source_id}</td>
                    <td>{s.v_class}</td>
                    <td>{s.n_class}</td>
                    <td><ABadge cls={s.a_class} /></td>
                    <td style={{ fontSize: 12 }}>{s.max_weight}</td>
                    <td><TargetBadges targets={s.signal_targets} /></td>
                    <td style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{s.functional_role}</td>
                    <td style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{s.decay_days ? `${s.decay_days}gg` : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* SECTION 2 — Pending Proposals */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Proposte Pending ({proposals.length})
            </h3>
            <button onClick={() => setShowLegend(l => !l)}
              style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--surface)', border: '1px solid rgba(175,169,236,0.3)',
                color: 'var(--accent)', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>?</button>
          </div>

          {showLegend && (
            <div style={{ background: 'var(--surface)', border: '1px solid rgba(175,169,236,0.3)', borderRadius: 8, padding: '14px 18px', marginBottom: 16, fontSize: 12, color: 'var(--accent)', lineHeight: 1.7 }}>
              <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>Come funziona la calibrazione</div>
              <div>Ermene monitora le fonti nel tempo e misura quanto spesso anticipano correttamente gli eventi reali (infortuni, ritiri, ritorni).</div>
              <div style={{ marginTop: 8 }}><strong>Allineamento fonte</strong> — percentuale di segnali della fonte che si sono rivelati corretti su eventi reali verificati.</div>
              <div style={{ marginTop: 4 }}><strong>Deviazione dalla media</strong> — quanto questa fonte si discosta dalle altre fonti della stessa categoria. Sopra +15% → proposta di upgrade. Sotto -15% → proposta di downgrade.</div>
              <div style={{ marginTop: 4 }}><strong>Campione</strong> — numero di eventi verificati su cui si basa il calcolo. Minimo 50 per proporre un upgrade, 20 per un downgrade.</div>
            </div>
          )}

          {proposals.length === 0 && (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13,
              background: 'var(--surface-raised)', borderRadius: 8, border: '1px solid var(--border)' }}>
              Nessuna proposta pending — il modello e calibrato correttamente
            </div>
          )}
          {proposals.map(p => {
            const noteVal = notes[p.proposal_id] || '';
            const canSubmit = noteVal.trim().length >= 10;
            const isSaving = saving[p.proposal_id];
            const oarPct = p.oar != null ? (p.oar * 100) : 0;
            const baselinePct = p.baseline_deviation != null && p.oar != null ? ((p.oar - p.baseline_deviation) * 100) : 0;
            const devPct = p.baseline_deviation != null ? (p.baseline_deviation * 100) : 0;
            const isAboveThreshold = Math.abs(devPct) >= 15;
            const hasSufficientSample = p.direction === 'upgrade' ? p.n_observations >= 50 : p.n_observations >= 20;
            return (
              <div key={p.proposal_id} style={{
                background: 'var(--surface-raised)', borderRadius: 8, padding: 16, marginBottom: 12,
                border: '1px solid var(--border)',
                borderLeft: `4px solid ${DIR_COLORS[p.direction] || 'var(--border)'}`,
              }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14 }}>{p.source_id}</span>
                  <span style={{ background: (DIR_COLORS[p.direction] || '#6b7280') + '22', color: DIR_COLORS[p.direction],
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>
                    {p.direction} [{p.axis}]
                  </span>
                  <span style={{ background: (CONF_COLORS[p.confidence] || '#6b7280') + '22', color: CONF_COLORS[p.confidence],
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                    {p.confidence}
                  </span>
                </div>

                {/* Triplette */}
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
                  Corrente: V={p.current_v_class} N={p.current_n_class} A={p.current_a_class}
                  {' \u2192 '}
                  Proposto: V=<strong style={{ color: p.axis === 'V' ? DIR_COLORS[p.direction] : 'inherit' }}>{p.proposed_v_class}</strong>
                  {' '}N=<strong style={{ color: p.axis === 'N' ? DIR_COLORS[p.direction] : 'inherit' }}>{p.proposed_n_class}</strong>
                  {' '}A=<strong style={{ color: p.axis === 'A' ? DIR_COLORS[p.direction] : 'inherit' }}>{p.proposed_a_class}</strong>
                </div>

                {/* Alignment bars */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: 'var(--accent)' }}>Allineamento fonte</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--risk-low)' }}>{oarPct.toFixed(0)}%</span>
                    </div>
                    <div style={{ width: '100%', height: 6, background: 'var(--surface-raised)', borderRadius: 3 }}>
                      <div style={{ width: `${Math.min(100, oarPct)}%`, height: '100%', background: 'var(--risk-low)', borderRadius: 3 }} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: 'var(--accent)' }}>Media categoria {p[`current_${p.axis.toLowerCase()}_class`]}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)' }}>~{Math.max(0, baselinePct).toFixed(0)}%</span>
                    </div>
                    <div style={{ width: '100%', height: 6, background: 'var(--surface-raised)', borderRadius: 3 }}>
                      <div style={{ width: `${Math.min(100, Math.max(0, baselinePct))}%`, height: '100%', background: 'var(--accent)', borderRadius: 3 }} />
                    </div>
                  </div>

                  {/* Deviation + sample badges */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      Deviazione: <strong style={{ color: devPct > 0 ? '#4ADE80' : '#F87171' }}>{devPct > 0 ? '+' : ''}{devPct.toFixed(0)}%</strong> {devPct > 0 ? 'sopra' : 'sotto'} la media {devPct > 0 ? '\u2191' : '\u2193'}
                    </span>
                    {isAboveThreshold && (
                      <span style={{ background: 'rgba(74,222,128,0.15)', color: '#4ADE80', padding: '1px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600 }}>
                        Sopra soglia {'\u00B1'}15%
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      Basato su <strong>{p.n_observations}</strong> eventi verificati
                    </span>
                    <span style={{ background: hasSufficientSample ? 'rgba(74,222,128,0.15)' : 'rgba(248,113,113,0.15)',
                      color: hasSufficientSample ? '#4ADE80' : '#F87171',
                      padding: '1px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600 }}>
                      {hasSufficientSample ? '\u2713 Campione sufficiente' : '\u2717 Campione insufficiente'}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input placeholder="Nota obbligatoria (min 10 caratteri)..." value={noteVal}
                    onChange={e => setNotes(n => ({ ...n, [p.proposal_id]: e.target.value }))}
                    style={{ flex: 1, height: 30, padding: '0 10px', borderRadius: 6, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: 12 }} />
                  <button disabled={!canSubmit || isSaving} onClick={() => handleDecide(p.proposal_id, 'approved')}
                    style={{ padding: '4px 12px', fontSize: 11, background: canSubmit ? '#4ADE80' : 'transparent', color: canSubmit ? '#0E0C1E' : '#6b7280', border: canSubmit ? 'none' : '1px solid var(--border)', borderRadius: 6, cursor: canSubmit ? 'pointer' : 'not-allowed', fontWeight: 600 }}>
                    Approva</button>
                  <button disabled={!canSubmit || isSaving} onClick={() => handleDecide(p.proposal_id, 'rejected')}
                    style={{ padding: '4px 12px', fontSize: 11, background: 'transparent', color: canSubmit ? '#F87171' : '#6b7280', border: `1px solid ${canSubmit ? '#F87171' : 'var(--border)'}`, borderRadius: 6, cursor: canSubmit ? 'pointer' : 'not-allowed', fontWeight: 600 }}>
                    Rifiuta</button>
                  <button disabled={!canSubmit || isSaving} onClick={() => handleDecide(p.proposal_id, 'deferred')}
                    style={{ padding: '4px 12px', fontSize: 11, background: 'transparent', color: '#9CA3AF', border: '1px solid var(--border)', borderRadius: 6, cursor: canSubmit ? 'pointer' : 'not-allowed', fontWeight: 600 }}>
                    Rimanda</button>
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 6 }}>
                  L'approvazione aggiornera il peso della fonte nel modello al prossimo aggiornamento
                </div>
              </div>
            );
          })}
        </div>

        {/* SECTION 3 — Approval Log */}
        <div>
          <h3 style={{ margin: '0 0 12px 0', color: 'var(--text-primary)', fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Log Approvazioni ({logs.length})
          </h3>
          {logs.length === 0 ? (
            <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>Nessuna decisione registrata</div>
          ) : (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table className="admin-table">
                <thead>
                  <tr><th>Data</th><th>Decisione</th><th>Operatore</th><th>Nota</th></tr>
                </thead>
                <tbody>
                  {logs.map(l => (
                    <tr key={l.log_id}>
                      <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{l.timestamp?.split('T')[0]}</td>
                      <td><span style={{ color: DIR_COLORS[l.decision] || '#9CA3AF', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{l.decision}</span></td>
                      <td style={{ fontSize: 11 }}>{l.operator}</td>
                      <td style={{ fontSize: 11, color: 'var(--text-secondary)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
